import React from 'react';
import { Paper, Typography, Box, Grid, CircularProgress } from '@material-ui/core';
import { SearchOutlined as SearchIcon } from '@material-ui/icons';
import { useMetricsStyles } from './styles/metrics.styles';
import { usePrimitiveMetricsData } from './hooks/usePrimitiveMetricsData';
import { MetricCard } from './components/MetricCard';
import { MetricModal } from './components/MetricModal';
import { NamespaceGroup } from './types/metrics.types';

export const PrimitiveMetrics = React.memo((): JSX.Element => {
  const classes = useMetricsStyles();
  const [selectedPrimitive, setSelectedPrimitive] = React.useState<NamespaceGroup | null>(null);

  // Use custom hook for data fetching and processing
  const { website, isLoading, primitiveSections } = usePrimitiveMetricsData();

  const handlePrimitiveClick = (primitive: NamespaceGroup) => {
    setSelectedPrimitive(primitive);
  };

  const handleCloseModal = () => {
    setSelectedPrimitive(null);
  };

  // Show loading state
  if (isLoading) {
    return (
      <Paper className={classes.paper}>
        <Box className={classes.header}>
          <Box className={classes.headerContent}>
            <SearchIcon className={classes.headerIcon} />
            <Typography variant="h4" className={classes.headerTitle}>
              Primitive Point in Time Metrics
            </Typography>
          </Box>
        </Box>
        <Box className={classes.content}>
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        </Box>
      </Paper>
    );
  }

  // Show error state
  if (!website || primitiveSections.length === 0) {
    return (
      <Paper className={classes.paper}>
        <Box className={classes.header}>
          <Box className={classes.headerContent}>
            <SearchIcon className={classes.headerIcon} />
            <Typography variant="h4" className={classes.headerTitle}>
              Primitive Point in Time Metrics
            </Typography>
          </Box>
        </Box>
        <Box className={classes.content}>
          <Box className={classes.loadingContainer}>
            <Typography color="textSecondary">
              {!website ? 'No website selected' : 'No primitives found in timeline'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={`${classes.primitiveMetricsPaper}`}>
      <Box className={classes.header}>
        <Box className={classes.headerContent}>
          <SearchIcon className={classes.headerIcon} />
          <Typography variant="h4" className={classes.headerTitle}>
            Primitive Point in Time Metrics
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box className={classes.content}>
        <Grid container spacing={3}>
          {primitiveSections.map((section, index) => 
            <MetricCard
              key={index}
              section={section}
              index={index}
              onClick={handlePrimitiveClick}
            />
          )}
        </Grid>
      </Box>

      {/* Modal */}
      <MetricModal
        selectedPrimitive={selectedPrimitive}
        onClose={handleCloseModal}
      />
    </Paper>
  );
});

PrimitiveMetrics.displayName = 'PrimitiveMetrics';
